import React, { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { PageLoader } from "./components/common/LoadingFallback";
import AuthReadyGuard from "./components/common/AuthReadyGuard";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { NotFoundPage } from "./components/fallback/fallbackPage";
import { NotificationProvider } from "./context/NotificationContext";
import { ChatProvider } from "./context/ChatContext";
//import { useDarkMode } from "./context/DarkModeContext";

// Eager load critical components (navbar, chatbot)
import Navbar from "./components/common/navbar";
import SellerNavbar from "./components/seller/sellerNavbar";
import CustomerSupportChatbot from "./components/common/customerSupportChatbot";
import SellerChatbot from "./components/seller/SellerChatbot";

import ScrollToTop from "./components/common/ScrollToTop";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import ScrollToTopButton from "./components/common/ScrollToTopButton";
import SellerGuard from "./components/seller/SellerGuard";
import ProtectedRoute from "./components/common/ProtectedRoute";
import AuthGatePopup from "./components/common/AuthGatePopup";
import PasswordSetupPrompt from "./components/common/PasswordSetupPrompt";
import RecentVerificationDialog from "./components/common/RecentVerificationDialog";
import { LocationProvider } from "./components/common/location/useGeolocation";

import WishlistAuthPopup from "./components/common/WishlistAuthPopup";
import { useWishlist } from "./context/WishlistContext";


const THEME_STORAGE_KEY = "theme";
const THEME_SOURCE_STORAGE_KEY = "theme_source";
const THEME_MODES = {
  SYSTEM: "system",
  LIGHT: "light",
  DARK: "dark",
};
const THEME_SOURCES = {
  SYSTEM: "system",
  MANUAL: "manual",
};

const HomePage = lazy(() => import("./pages/index"));
const TypesenseProductsPage = lazy(() => import("./pages/typesenseProducts"));
const AboutPage = lazy(() => import("./pages/about"));
const ContactPage = lazy(() => import("./pages/contact"));
const CartPage = lazy(() => import("./pages/cart"));
const LoginPage = lazy(() => import("./pages/SignIn"));
const SignupPage = lazy(() => import("./pages/SignUp"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPassword"));
const DemoPage = lazy(() => import("./pages/demo"));
const WishList = lazy(() => import("./pages/wishList"));
const ShortTermRentals = lazy(() => import("./pages/shortTermRentals"));
const LongTermRentals = lazy(() => import("./pages/longTermRentals"));
const PrivacyPolicy = lazy(() => import("./pages/privacy"));
const TermsOfService = lazy(() => import("./pages/terms"));
const CookiesPolicy = lazy(() => import("./pages/cookies"));
const Messages = lazy(() => import("./pages/messages"));
const ProductIndex = lazy(
  () => import("./components/productsCategoryCard/productIndex"),
);
const BlogPage = lazy(() => import("./pages/blog"));

// Lazy load pages - Seller Routes
const Seller = lazy(() => import("./pages/seller"));
const SellerListings = lazy(() => import("./pages/seller/sellerListings"));
const ListingPaymentReturn = lazy(() => import("./pages/seller/ListingPaymentReturn"));
const ListingPublishPaymentPage = lazy(() => import("./pages/seller/ListingPublishPaymentPage"));
const SellerPlansPage = lazy(() => import("./pages/seller/SellerPlansPage"));
const SellerSettings = lazy(() => import("./pages/seller/sellerSettings"));
const SellerNotifications = lazy(
  () => import("./pages/seller/sellerNotifications"),
);
const SellerMessages = lazy(() => import("./pages/seller/sellerMessages"));

const SellerSupport = lazy(() => import("./pages/seller/sellerSupport"));

// Lazy load components
const AddProduct = lazy(() => import("./components/seller/addProduct"));
const EditProduct = lazy(() => import("./components/seller/editProduct"));

// Admin Routes
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const ProductManagement = lazy(() => import("./pages/admin/ProductManagement"));
const ReviewManagement = lazy(() => import("./pages/admin/ReviewManagement"));
const SliderManagement = lazy(() => import("./pages/admin/SliderManagement"));

// Component to conditionally render navbar based on route
const ConditionalNavbar = ({ toggleDarkMode, darkMode }) => {
  const location = useLocation();
  const isSellerRoute = location.pathname.startsWith("/seller");
  const isAuthRoute = ["/login", "/signup", "/forgot-password"].includes(location.pathname);

  if (isAuthRoute) return null;

  return isSellerRoute ? (
    <SellerNavbar toggleDarkMode={toggleDarkMode} darkMode={darkMode} />
  ) : (
    <Navbar toggleDarkMode={toggleDarkMode} darkMode={darkMode} />
  );
};

const LegacyAccountRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/account${search}`} replace />;
};



// Component to conditionally render chatbot only on user pages, and detect product details page
const ConditionalChatbot = () => {
  const location = useLocation();
  const isSellerRoute = location.pathname.startsWith("/seller");
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isMessagesRoute =
    location.pathname.startsWith("/messages") ||
    location.pathname.startsWith("/seller/messages");

  // Detect product details page by path
  const isProductDetailsPage = /^\/products\/product-details\//.test(
    location.pathname,
  );

  // Hide the global support chatbot on direct messaging pages to prevent UI overlap
  if (isMessagesRoute) {
    return null;
  }

  // Only show chatbot on user-facing pages, not seller or admin pages
  if (isSellerRoute) {
    return <SellerChatbot />;
  }
  return !isAdminRoute ? (
    <CustomerSupportChatbot isProductDetailsPage={isProductDetailsPage} />
  ) : null;
};

const PublicOnlyRoute = ({ children, sellerDefault = false }) => {
  const { user, isAuthenticated, authReady } = useAuth();
  const location = useLocation();

  if (!authReady) return <AuthReadyGuard isPending />;

  if (!isAuthenticated) {
    return children;
  }

  const fromState = location.state?.from;
  const fromQuery = new URLSearchParams(location.search).get("from");
  const candidate = fromState || fromQuery;
  const safeRequestedPath =
    typeof candidate === "string" &&
      candidate.startsWith("/") &&
      !candidate.startsWith("//") &&
      candidate !== "/login"
      ? candidate
      : null;

  if (safeRequestedPath) {
    return <Navigate to={safeRequestedPath} replace />;
  }

  const fallbackPath = sellerDefault
    ? "/seller"
    : user?.roles?.includes("admin")
      ? "/admin"
      : "/";
  return <Navigate to={fallbackPath} replace />;
};

const AppLayout = ({ darkMode, toggleDarkMode }) => {
  const { isAuthenticated, isGuest, authReady } = useAuth();
  const [showAuthPopup, setShowAuthPopup] = React.useState(false);
  const location = useLocation();

const {showWishlistAuth,setShowWishlistAuth } = useWishlist();
  useEffect(() => {
    if (authReady && !isAuthenticated && !isGuest) {
      // Don't show popup on login or signup pages
      const authPages = ["/login", "/signup", "/seller/signup", "/forgot-password"];
      if (authPages.includes(location.pathname)) return;

      const hasDismissedForever = localStorage.getItem("auth_gate_dismissed");
      const hasShownPopup = sessionStorage.getItem("auth_gate_shown");

      if (!hasShownPopup && !hasDismissedForever) {
        const timer = setTimeout(() => {
          setShowAuthPopup(true);
          sessionStorage.setItem("auth_gate_shown", "true");
        }, 5000);
        return () => clearTimeout(timer);
      }
    }
  }, [authReady, isAuthenticated, isGuest, location.pathname]);

  return (
    <AuthReadyGuard isPending={!authReady} loader={<PageLoader />}>
      <NotificationProvider>
        <ChatProvider>
          <div className="min-h-screen md:mb-0 bg-app text-app !transition-none">
            <ConditionalNavbar
              darkMode={darkMode}
              toggleDarkMode={toggleDarkMode}
            />


            <ErrorBoundary>
              {/* Wrap routes with Suspense for lazy loading */}
              <Suspense fallback={<PageLoader />}>
                <Routes>

                  {/* Demo Route - To test lazy loading and fallbacks */}
                  <Route path="/demo" element={<DemoPage />} />

                  {/* user routes */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/products" element={<TypesenseProductsPage />} />
                  <Route path="/typesense-products" element={<TypesenseProductsPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route
                    path="/login/*"
                    element={
                      <PublicOnlyRoute>
                        <LoginPage />
                      </PublicOnlyRoute>
                    }
                  />
                  <Route path="/signup/*" element={<SignupPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/wishlist" element={<WishList />} />
                  <Route path="/services/short-term" element={<ShortTermRentals />} />
                  <Route path="/services/long-term" element={<LongTermRentals />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/cookies" element={<CookiesPolicy />} />
                  <Route path="/messages" element={<Messages />} />
                  <Route path="/messages/:chatId" element={<Messages />} />
                  <Route
                    path="/products/product-details/:productId"
                    element={<ProductIndex />}
                  />

                  {/* Settings - accessible to ALL logged-in users */}
                  <Route path="/account" element={<SellerSettings />} />
                  <Route path="/settings" element={<LegacyAccountRedirect />} />

                  {/* Seller Routes */}
                  {/* Add Listing - accessible to ALL logged-in users */}
                  <Route
                    path="/seller/listings/add"
                    element={
                      <ProtectedRoute isAllowed={true}>
                        <AddProduct />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/support" element={<SellerSupport />} />

                  {/* Protected Seller Routes (seller role required) */}
                  <Route element={<SellerGuard />}>
                    <Route path="/seller" element={<Seller />} />
                    <Route path="/seller/listings" element={<SellerListings />} />
                    <Route path="/seller/plans" element={<SellerPlansPage />} />
                    <Route
                      path="/seller/listings/payment-return"
                      element={
                        <ProtectedRoute isAllowed={true}>
                          <ListingPaymentReturn />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/seller/listings/:id/publish"
                      element={
                        <ProtectedRoute isAllowed={true}>
                          <ListingPublishPaymentPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/seller/listings/edit/:id"
                      element={<EditProduct />}
                    />
                    <Route path="/seller/settings" element={<LegacyAccountRedirect />} />
                    <Route
                      path="/seller/notifications"
                      element={<SellerNotifications />}
                    />
                    <Route
                      path="/seller/messages"
                      element={<SellerMessages />}
                    />
                    <Route
                      path="/seller/messages/:chatId"
                      element={<SellerMessages />}
                    />
                  </Route>

                  {/* Admin Routes */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="users" element={<UserManagement />} />
                    <Route path="products" element={<ProductManagement />} />
                    <Route path="reviews" element={<ReviewManagement />} />
                    <Route path="sliders" element={<SliderManagement />} />
                    <Route
                      path="settings"
                      element={
                        <div className="p-8">
                          <h1 className="text-2xl font-bold">Admin Settings</h1>
                          <p className="mt-4">Configuration options coming soon...</p>
                        </div>
                      }
                    />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>

            {/* Customer Support Chatbot - only on user pages */}
            <ConditionalChatbot />

            {/* Auth Gate Popup */}
            {showAuthPopup && (
              <AuthGatePopup onClose={() => setShowAuthPopup(false)} />
            )}

            {showWishlistAuth && (
              <WishlistAuthPopup
              onClose={() => setShowWishlistAuth(false)}
              />
            )}
            
            <PasswordSetupPrompt />
          </div>

          <ScrollToTop />
          <ScrollToTopButton />
        </ChatProvider>
      </NotificationProvider>
    </AuthReadyGuard>
  );
};

const App = () => {
  //const { darkMode } = useDarkMode();
  const [themePreference, setThemePreference] = React.useState(THEME_MODES.SYSTEM);
  const [darkMode, setDarkMode] = React.useState(false);

  const getSystemPrefersDark = React.useCallback(
    () => window.matchMedia("(prefers-color-scheme: dark)").matches,
    [],
  );

  const resolveDarkMode = React.useCallback(
    (preference) => {
      if (preference === THEME_MODES.DARK) return true;
      if (preference === THEME_MODES.LIGHT) return false;
      return getSystemPrefersDark();
    },
    [getSystemPrefersDark],
  );

  const applyTheme = React.useCallback((isDark) => {
    const html = document.documentElement;
    html.classList.toggle("dark", isDark);
    html.setAttribute("data-theme", isDark ? "dark" : "light");
    html.style.colorScheme = isDark ? "dark" : "light";
    localStorage.setItem("darkMode", String(isDark));
    setDarkMode(isDark);

    window.dispatchEvent(
      new CustomEvent("darkmodechange", { detail: { darkMode: isDark } }),
    );

    const metaThemeColor = document.querySelector("meta[name='theme-color']");
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", isDark ? "#111827" : "#ffffff");
    }
  }, []);

  //Load saved theme on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    const savedThemeSource = localStorage.getItem(THEME_SOURCE_STORAGE_KEY);
    let nextPreference = THEME_MODES.SYSTEM;

    if (savedTheme === THEME_MODES.SYSTEM) {
      nextPreference = THEME_MODES.SYSTEM;
    } else if (
      (savedTheme === THEME_MODES.DARK || savedTheme === THEME_MODES.LIGHT) &&
      savedThemeSource === THEME_SOURCES.MANUAL
    ) {
      nextPreference = savedTheme;
    }

    setThemePreference(nextPreference);
    applyTheme(resolveDarkMode(nextPreference));
  }, [applyTheme, resolveDarkMode]);

  useEffect(() => {
    applyTheme(resolveDarkMode(themePreference));
  }, [themePreference, applyTheme, resolveDarkMode]);

  // Sync theme across browser tabs
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== THEME_STORAGE_KEY) return;
      if (
        event.newValue !== THEME_MODES.DARK &&
        event.newValue !== THEME_MODES.LIGHT &&
        event.newValue !== THEME_MODES.SYSTEM
      ) {
        setThemePreference(THEME_MODES.SYSTEM);
        return;
      }
      setThemePreference(event.newValue);
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    const handleUserTheme = (event) => setThemePreference(event.detail);
    window.addEventListener("akkorent:theme", handleUserTheme);
    return () => window.removeEventListener("akkorent:theme", handleUserTheme);
  }, []);

  // Follow operating-system theme while in system mode
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemThemeChange = (event) => {
      if (themePreference === THEME_MODES.SYSTEM) {
        applyTheme(event.matches);
      }
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleSystemThemeChange);
      return () => mediaQuery.removeEventListener("change", handleSystemThemeChange);
    }

    mediaQuery.addListener(handleSystemThemeChange);
    return () => mediaQuery.removeListener(handleSystemThemeChange);
  }, [themePreference, applyTheme]);

  // Persist preference and legacy bool for compatibility
  const toggleDarkMode = () => {
  setThemePreference((prev) => {
    let newPreference;
    if (prev === THEME_MODES.SYSTEM) {
      newPreference = darkMode ? THEME_MODES.LIGHT : THEME_MODES.DARK;
    } else {
      newPreference = prev === THEME_MODES.DARK ? THEME_MODES.LIGHT : THEME_MODES.DARK;
    }
    
    localStorage.setItem(THEME_STORAGE_KEY, newPreference);
    localStorage.setItem(THEME_SOURCE_STORAGE_KEY, THEME_SOURCES.MANUAL);
    localStorage.setItem("darkMode", String(newPreference === THEME_MODES.DARK));
    
    return newPreference;
  });
};

  return (
    <AuthProvider>
      <RecentVerificationDialog />
      <LocationProvider>
        <Router>
          <AppLayout darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
        </Router>
      </LocationProvider>
    </AuthProvider>
  );
};

export default App;
